<template>
  <div>
    <header
      class="fixed top-0 left-0 right-0 h-14 bg-brand-600 z-40 lg:hidden flex items-center px-4"
    >
      <button
        @click="toggleSidebar"
        class="text-white p-2 hover:bg-brand-700 rounded-md transition-colors -ml-2"
        aria-label="Toggle navigation"
      >
        <svg
          v-if="!isSidebarOpen"
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>

        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <a :href="homePath"> <IdhLogo class="h-8 w-24" /> </a>
      <h1 class="text-white text-lg font-semibold ml-2">Benchmark Finder</h1>

      <LanguageSelector
        class="ml-auto"
        :languages="languages"
        :current-locale="currentLocale"
        variant="compact"
      />
    </header>

    <div
      v-if="isSidebarOpen"
      @click="closeSidebar"
      class="fixed top-14 left-0 right-0 bottom-0 bg-black bg-opacity-50 z-30 md:hidden transition-opacity"
    ></div>

    <nav
      :class="[
        'fixed left-0 bg-brand-600 flex flex-col z-30 transition-transform duration-300 ease-in-out',
        'top-14 h-[calc(100vh-3.5rem)] w-full sm:w-72 lg:top-0 lg:h-full py-4 lg:pt-8 lg:pb-4 px-5',
        {
          '-translate-x-full lg:translate-x-0': !isSidebarOpen,
          'translate-x-0': isSidebarOpen,
        },
      ]"
    >
      <a class="hidden lg:block flex-none" :href="homePath"> <IdhLogo /> </a>

      <div class="lg:mt-8 pb-4">
        <h4
          class="text-white text-opacity-50 uppercase text-xs font-semibold mb-1"
        >
          <Translation
            name="benchmarks_sidebar.recently_visited"
            default="Recently visited"
          />
        </h4>
        <a
          v-if="recentBenchmark"
          :href="`/${locale}/benchmark-finder/${recentBenchmark.country_code.toLowerCase()}`"
          @click="computedRecentlyVisited(recentBenchmark)"
          class="block text-white py-2 px-3 rounded-lg hover:bg-brand-700 cursor-pointer"
        >
          {{ recentBenchmark.name }}
        </a>
      </div>
      <div class="pb-2">
        <h4
          class="text-white text-opacity-50 uppercase text-xs font-semibold pb-1"
        >
          <Translation
            name="benchmarks_sidebar.search_countries"
            default="Search countries"
          />
        </h4>
        <div
          class="flex items-center w-full px-3 py-2 rounded-lg bg-brand-700 gap-x-2 border-2 border-solid border-brand-600 focus-within:border-brand-500 transition-colors duration-200 ease-in-out"
        >
          <SearchWhiteIcon class="flex-none" />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="searchPlaceholder"
            class="bg-brand-700 text-white placeholder-white/50 focus:outline-none"
          />
        </div>
      </div>
      <div class="flex flex-col overflow-y-scroll scrollbar scrollbar-sidebar">
        <template v-if="!searchQuery.trim()">
          <a
            v-for="benchmark in benchmarks"
            :key="benchmark.country_code"
            :href="`/${locale}/benchmark-finder/${benchmark.country_code.toLowerCase()}`"
            @click="computedRecentlyVisited(benchmark)"
            class="text-white py-2 px-3 rounded-lg hover:bg-brand-700 cursor-pointer"
          >
            {{ benchmark.name }}
          </a>
        </template>

        <template v-else>
          <a
            v-if="filteredBenchmarks.length > 0"
            v-for="benchmark in filteredBenchmarks"
            :key="benchmark.country_code"
            :href="`/${locale}/benchmark-finder/${benchmark.country_code.toLowerCase()}`"
            @click="computedRecentlyVisited(benchmark)"
            class="text-white py-2 px-3 rounded-lg hover:bg-brand-700 cursor-pointer"
          >
            {{ benchmark.name }}
          </a>

          <div
            v-else
            class="text-white text-opacity-60 h-[100px] flex flex-col py-4 border-2 border-dashed border-brand-500 rounded-lg gap-2 items-center"
          >
            <div
              class="flex-none h-10 w-10 rounded-full bg-brand-500 flex items-center justify-center"
            >
              <SearchWhiteIcon class="w-4 h-4" />
            </div>
            <Translation
              name="benchmarks_sidebar.search_no_results"
              default="No Results Found"
            />
          </div>
        </template>
      </div>

      <div class="mt-4 pt-4 border-t border-brand-500 hidden lg:block">
        <LanguageSelector
          :languages="languages"
          :current-locale="currentLocale"
          variant="full"
        />
      </div>
    </nav>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import SearchWhiteIcon from "../icons/SearchWhiteIcon.vue";
import IdhLogo from "../icons/IdhLogo.vue";
import LanguageSelector from "./LanguageSelector.vue";
import { useTranslation } from "../aft/useTranslations";

const props = defineProps({
  benchmarks: {
    type: Array,
    required: true,
  },
  homePath: {
    type: String,
    required: true,
  },
  languages: {
    type: Array,
    required: true,
  },
  currentLocale: {
    type: String,
    required: true,
  },
});

const { _t: translate } = useTranslation();

const searchPlaceholder = translate(
  "benchmarks_sidebar.search_placeholder",
  "Search",
);

const isSidebarOpen = ref(false);

const locale = window.location.pathname.split("/")[1] || "en";

const searchQuery = ref("");
const recentBenchmark = ref(null);
const recentBenchmarkQueued = ref(null);

onMounted(() => {
  const stored = localStorage.getItem("recentBenchmarks");
  if (stored) {
    const parsed = JSON.parse(stored);
    recentBenchmark.value = parsed.recent;
    recentBenchmarkQueued.value = parsed.queued;
  }
});

const computedRecentlyVisited = (benchmark) => {
  if (!recentBenchmark.value && !recentBenchmarkQueued.value) {
    recentBenchmark.value = benchmark;
    recentBenchmarkQueued.value = benchmark;
  } else {
    if (recentBenchmarkQueued.value) {
      recentBenchmark.value = recentBenchmarkQueued.value;
      recentBenchmarkQueued.value = benchmark;
    }
  }

  localStorage.setItem(
    "recentBenchmarks",
    JSON.stringify({
      recent: recentBenchmark.value,
      queued: recentBenchmarkQueued.value,
    }),
  );
};

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
  if (isSidebarOpen.value) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
};

const closeSidebar = () => {
  isSidebarOpen.value = false;
  document.body.style.overflow = "";
};

const filteredBenchmarks = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.benchmarks;
  }

  const query = searchQuery.value.toLowerCase();
  return props.benchmarks.filter((b) => b.name.toLowerCase().startsWith(query));
});
</script>
